import { storage } from '@/helpers/storage'
import { setLocation } from '@/helpers/routes'
import { Sentry } from '@/services/sentry'
import type { SignupModal17906OpenOptions } from './types'

const SIGNUP_PATH = '/app/signup'
const APP_BOOK_PATH_PREFIX = '/app/book/'
const EMAIL_SIGNUP_CTA = 'email_signup'
const AUTH_TOKEN_KEY = 'auth_token'

export type FallbackRedirect = () => void

/**
 * Normalize static page paths to route-like paths.
 *
 * Convert paths like /index.html to the route shape used by the app.
 * This is mainly needed for local static-page testing. In production,
 * CloudFront already rewrites public page requests to the canonical route.
 */
export function normalizedPathname(pathname: string): string {
  if (pathname.endsWith('/index.html')) {
    const normalizedIndexPath = pathname.slice(0, -'/index.html'.length)
    pathname = normalizedIndexPath || '/'
  }

  if (pathname !== '/' && pathname.endsWith('/')) {
    return pathname.slice(0, -1)
  }
  return pathname
}

/**
 * Return an intercepted URL when the anchor points to a signup entry path.
 */
export function getSignupUrl(anchor: HTMLAnchorElement | null): URL | null {
  if (!anchor || anchor.target === '_blank') {
    return null
  }

  const url = new URL(anchor.href, window.location.href)
  if (url.origin !== window.location.origin || !isInterceptedPath(url)) {
    return null
  }

  return url
}

/**
 * Return true when the URL should open the signup modal on public pages.
 */
export function isInterceptedPath(url: URL): boolean {
  const pathname = normalizedPathname(url.pathname)
  return pathname === SIGNUP_PATH || pathname.startsWith(APP_BOOK_PATH_PREFIX)
}

/**
 * Find the closest matching element for the event target.
 */
export function getClosestElement<T extends Element>(
  target: EventTarget | null,
  selector: string,
): T | null {
  const elementTarget = toElement(target)
  return (elementTarget?.closest(selector) as T | null) ?? null
}

/**
 * Convert an event target to an element when possible.
 */
export function toElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) {
    return target
  }

  if (target instanceof Node) {
    return target.parentElement
  }

  return null
}

/**
 * Open the modal for variant B and fall back to redirect otherwise.
 */
export async function openModalOrRedirect(
  signupUrl: URL,
  fallbackRedirect: FallbackRedirect,
  email?: string,
): Promise<void> {
  try {
    if (storage.getItem(AUTH_TOKEN_KEY) !== null) {
      fallbackRedirect()
      return
    }

    // Load the modal island only when an intercepted CTA actually opens it.
    // Public pages keep the normal redirect path in the initial bundle and
    // only fetch the modal/auth UI for the variant-B experience.
    const { openSignupModal17906 } = await import(
      // eslint-disable-next-line
      /* webpackChunkName: "signup-modal-island" */
      '@/pages-signup-modal/init'
    )
    const openOptions: SignupModal17906OpenOptions = {
      email,
      redirectHref: redirectAfterSignupHref(signupUrl),
      ctaId: signupUrl.searchParams.get('cta'),
    }
    await openSignupModal17906(openOptions)
  } catch (error) {
    Sentry?.withScope((scope) => {
      scope.setExtra('signupUrl', signupUrl.toString())
      scope.setExtra('email', email)
      Sentry.captureException(error)
    })
    fallbackRedirect()
  }
}

/**
 * Return the post-signup redirect target for intercepted gated app links.
 */
export function redirectAfterSignupHref(signupUrl: URL): string | undefined {
  const pathname = normalizedPathname(signupUrl.pathname)
  if (pathname === SIGNUP_PATH) {
    return undefined
  }

  return `${signupUrl.pathname}${signupUrl.search}${signupUrl.hash}`
}

/**
 * Build the signup URL used when opening the modal from the email form.
 */
export function createEmailSignupUrl(emailValue: string): URL {
  const signupUrl = new URL(SIGNUP_PATH, window.location.origin)
  if (emailValue) {
    signupUrl.searchParams.set('email', emailValue)
  }
  signupUrl.searchParams.set('cta', EMAIL_SIGNUP_CTA)
  return signupUrl
}

/**
 * Redirect the browser to the signup URL.
 */
export function redirectToSignup(signupUrl: URL): void {
  setLocation(signupUrl.toString(), (): void => {})
}
